import Breadcrumb, { Crumb, Separator } from '@asphalt-react/breadcrumb';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useHistory, Link } from 'react-router-dom';
import LoadingPage from '../../common/pages/LoadingPage';
import {
    getIllustrationDetails,
    checkVpnConnection,
    getDarkroomStorageUrls,
    uploadFileToBucket,
    updateDarkroomUrlForDefaultIllustration,
    syncBcsIllustrationUrls,
} from '../api/illustrationsApi';
import IllustrationViewer from '../components/IllustrationViewer';
import routes from '../../common/models/route-constants';
import Flag from '@asphalt-react/flag';
import styles from './DefaultIllustrationDetailsPage.module.css';
import IllustrationType from '../models/IllustrationType';
import { formatTime } from '../../common/utils/generalUtils'
import { useUser } from "../../authentication/api/userApi";
import { UserContext } from '../../common/hooks/UserContext';
import {
    downloadSvgAndConvertToFile,
    getRasterImageFromSvg,
} from '../utils/illustrationTransformationUtils';

const VPN_ENVIRONMENT = process.env.REACT_APP_VPN_ENVIRONMENT;
const IS_INTEGRATION = process.env.REACT_APP_ENVIRONMENT === 'integration';
const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === 'prod';

function getBffUrl(env) {
    if (env === 'prod') {
        return process.env.REACT_APP_BFF_BASE_URL;
    }
    if (env === 'qa') {
        return (
            process.env.REACT_APP_QA_BFF_BASE_URL ||
            process.env.REACT_APP_INT_BFF_BASE_URL ||
            process.env.REACT_APP_BFF_BASE_URL
        );
    }
    return process.env.REACT_APP_INT_BFF_BASE_URL || process.env.REACT_APP_BFF_BASE_URL;
}

/* All user-facing texts on this page */
const texts = {
    breadcrumbAssets: 'assets',
    defaultIllustrationName: 'Default',
    regionalIllustrationsHeading: 'Regional Illustrations',
    lastUpdatedAtLabel: 'Last updated at',
    createdAtLabel: 'Created at',
    lastUpdatedByLabel: 'Last updated by',
    createdByLabel: 'Created by',
    alohaVersionLabel: 'Aloha Version',
    alohaReleaseDateLabel: 'Aloha Release Date',
    notAvailable: 'NA',
    loading: 'Loading...',
    vpnErrorTitle: 'VPN connection error',
    vpnErrorContent: `Please connect to ${VPN_ENVIRONMENT} VPN`,
    vpnErrorLog:
        'You are not connected to the VPN. Please connect to the correct vpn and try again.',
    vpnRequiredTitle: `${VPN_ENVIRONMENT} VPN required`,
    vpnRequiredContent: `You need to be connected to the ${VPN_ENVIRONMENT} VPN to upload or update regional illustrations.`,
    darkroomUrlGeneratedTitle: 'Darkroom URL generated',
    darkroomUrlGeneratedContent: (illustrationName) =>
        `Darkroom URL for ${illustrationName} has been generated successfully`,
    darkroomUrlGeneratedBcsSyncFailedContent: (illustrationName) =>
        `Darkroom URL for ${illustrationName} was generated, but BCS URLs could not be refreshed. Open BCS Config and re-save.`,
    darkroomUrlFailedTitle: 'Failed to generate Darkroom URL',
    darkroomUrlFailedContent: (errorMessage) =>
        `An error occured while generating the Darkroom URL : ${errorMessage}. Please try again.`,
};


//These regions are not maintained anymore
const EXCLUDED_REGION_IDENTIFIERS = ["VN", "TH"]

function DefaultIllustrationDetailsPage() {
    const clearHistoryNotificationState = (history) => {
        let state = { ...history.location.state };
        delete state.notification;
        history.replace({
            ...history.location,
            state,
        });
    };


    const { pathname } = useLocation();
    let history = useHistory();
    const location = useLocation();
    const [notification, setNotification] = useState(null);

    // Context state
    const { user } = useContext(UserContext);

    // Canvas used to rasterize the SVG for the Darkroom upload
    const canvasRef = useRef(null);

    // Illustration State
    const [illustrationDetailsObj, setIllustrationDetailsObj] = useState(null);
    const updatedBy = useUser(illustrationDetailsObj?.default?.last_updated_by);
    const createdBy = useUser(illustrationDetailsObj?.default?.created_by);

    // Page loading state
    const [isLoading, setIsLoading] = useState(true);
    const [isGeneratingDarkroomUrl, setIsGeneratingDarkroomUrl] =
        useState(false);

    // Params state
    const { id } = useParams();

    /**
     * Generates the Darkroom URL for the default illustration when it is
     * missing (e.g. the upload failed during asset creation). Mirrors the
     * Darkroom sequence of the update flow: download the stored SVG,
     * rasterize it, upload to Darkroom and save the public URL on Firestore.
     */
    const handleGenerateDarkroomUrl = async () => {
        setIsGeneratingDarkroomUrl(true);
        try {
            const isConnectedToVpn = await checkVpnConnection();
            if (!isConnectedToVpn) {
                setNotification({
                    title: texts.vpnErrorTitle,
                    content: texts.vpnErrorContent,
                    isSuccess: false,
                    isDanger: true,
                });
                return;
            }
            const file = await downloadSvgAndConvertToFile(
                illustrationDetailsObj.default.img_url,
                illustrationDetailsObj.combined_name
            );
            const blob = await getRasterImageFromSvg(file, canvasRef.current);
            const darkroomResponse = await getDarkroomStorageUrls(
                illustrationDetailsObj.combined_name
            );
            await uploadFileToBucket(
                darkroomResponse.data.data.upload_url,
                blob,
                user.idToken
            );
            await updateDarkroomUrlForDefaultIllustration(
                id,
                user.uid,
                darkroomResponse.data.data.public_url
            );
            
            let bcsSyncFailed = false;
            try {
                const configKey = illustrationDetailsObj.combined_name.toUpperCase();
                const newUrl = darkroomResponse.data.data.public_url;
                const isVariantSync = illustrationDetailsObj.variant_sync !== false;
                const darkUrl = isVariantSync
                    ? newUrl
                    : (illustrationDetailsObj.default.darkroom_public_url_dark ?? newUrl);
                const envsToSync = IS_PRODUCTION
                    ? [
                          { bffBaseUrl: getBffUrl('integration'), bcsEnv: 'integration' },
                          { bffBaseUrl: getBffUrl('qa'),          bcsEnv: 'qa' },
                          { bffBaseUrl: getBffUrl('prod'),        bcsEnv: undefined },
                      ]
                    : IS_INTEGRATION
                    ? [
                          { bffBaseUrl: getBffUrl('integration'), bcsEnv: 'integration' },
                          { bffBaseUrl: getBffUrl('qa'),          bcsEnv: 'qa' },
                      ]
                    : [{ bffBaseUrl: undefined, bcsEnv: undefined }];

                await Promise.all(
                    envsToSync.map(({ bffBaseUrl, bcsEnv }) =>
                        syncBcsIllustrationUrls(configKey, newUrl, darkUrl, user.idToken, bffBaseUrl, bcsEnv)
                    )
                );
            } catch (error) {
                console.error("BCS sync failed:", error);
                bcsSyncFailed = true;
            }

            const doc = await getIllustrationDetails(id);
            setIllustrationDetailsObj(doc);
            setNotification({
                title: texts.darkroomUrlGeneratedTitle,
                content: bcsSyncFailed
                    ? texts.darkroomUrlGeneratedBcsSyncFailedContent(
                          illustrationDetailsObj.combined_name
                      )
                    : texts.darkroomUrlGeneratedContent(
                          illustrationDetailsObj.combined_name
                      ),
                isSuccess: true,
                isDanger: false,
            });
        } catch (error) {
            console.error(error);
            setNotification({
                title: texts.darkroomUrlFailedTitle,
                content: texts.darkroomUrlFailedContent(error.message),
                isSuccess: false,
                isDanger: true,
            });
        } finally {
            setIsGeneratingDarkroomUrl(false);
        }
    };

    function getRegionalIllustrationListItem(
        regionalIllustration,
        defaultIllustrationImgUrl
    ) {
        return (
            <IllustrationViewer
                key={regionalIllustration.region_name}
                imgUrl={
                    regionalIllustration.same_as_default
                        ? defaultIllustrationImgUrl
                        : regionalIllustration.img_url
                }
                illustrationName={regionalIllustration.region_name}
                darkroomUrl={regionalIllustration.darkroom_public_url}
                showVariantToggle={false}
                illustrationType={IllustrationType.REGIONAL}
                onEditButtonClick={() => {
                    // Check if user is connected to the vpn
                    setIsLoading(true);
                    checkVpnConnection()
                        .then((isConnectedToVpn) => {
                            if (isConnectedToVpn) {
                                history.push(
                                    `${pathname}/${regionalIllustration.id}/${routes.UPDATE}`
                                );
                            } else {
                                setIsLoading(false);
                                console.error(texts.vpnErrorLog);
                                setNotification({
                                    title: texts.vpnErrorTitle,
                                    content: texts.vpnErrorContent,
                                    isSuccess: false,
                                    isDanger: true,
                                });
                            }
                        })
                        .catch((err) => {
                            console.error(err);
                        });
                    // TODO: For v2 redirect to region illustration details page
                }}
            />
        );
    }

    useEffect(() => {
        if (location.state && location['state'].notification) {
            setNotification(location.state.notification);
        }
        getIllustrationDetails(id)
            .then((doc) => {
                setIllustrationDetailsObj(doc);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error(error);
            });
    }, [setIllustrationDetailsObj, setIsLoading, id, history, location]);

    return (
        <React.Fragment>
            {isLoading ? (
                <LoadingPage />
            ) : (
                <React.Fragment>
                    <div className={`panel ${notification ? 'show' : ''}`}>
                        <div
                            className={`animateFlag ${notification ? 'visible' : ''
                                }`}>
                            <Flag
                                success={
                                    notification
                                        ? notification.isSuccess
                                        : false
                                }
                                danger={
                                    notification ? notification.isDanger : false
                                }
                                floating
                                title={notification ? notification.title : ''}
                                content={
                                    notification ? notification.content : ''
                                }
                                onDismiss={() => {
                                    setNotification(null);
                                    clearHistoryNotificationState(history);
                                }}
                            />
                        </div>
                    </div>
                    <canvas style={{ display: 'none' }} ref={canvasRef} />
                    <div className={styles.detailsPageContainer}>
                        <div className={styles.breadcrumbContainer}>
                            <Breadcrumb>
                                <Crumb
                                    text={texts.breadcrumbAssets}
                                    tagProps={{ to: '/' }}
                                    as={Link}
                                />
                                <Separator />
                                <Crumb
                                    text={illustrationDetailsObj.combined_name}
                                    tagProps={{
                                        to: `/${routes.ILLUSTRATIONS}/${routes.ASSETS}/${id}`,
                                    }}
                                    as={Link}
                                />
                            </Breadcrumb>
                        </div>
                        <div>
                            <h3 className={styles.heading3XL}>
                                {illustrationDetailsObj.combined_name}
                            </h3>
                            <div style={{ marginBottom: '2rem' }}>
                                {/* First row */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '20px',
                                    width: '100%'
                                }}>
                                    <div style={{ width: '45%' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{texts.lastUpdatedAtLabel}</div>
                                        <div style={{ margin: '0' }}>{formatTime(illustrationDetailsObj.updated_at)}</div>
                                    </div>
                                    <div style={{ width: '45%' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{texts.createdAtLabel}</div>
                                        <div style={{ margin: '0' }}>{formatTime(illustrationDetailsObj.created_at)}</div>
                                    </div>
                                </div>

                                {/* Second row */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '20px',
                                    width: '100%'
                                }}>
                                    <div style={{ width: '45%' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{texts.lastUpdatedByLabel}</div>
                                        <div style={{ margin: '0' }}>{updatedBy ? `${updatedBy.name}` : texts.loading}</div>
                                    </div>
                                    <div style={{ width: '45%' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{texts.createdByLabel}</div>
                                        <div style={{ margin: '0' }}>{createdBy ? `${createdBy.name}` : texts.loading}</div>
                                    </div>
                                </div>

                                {/* Third row */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%'
                                }}>
                                    <div style={{ width: '45%' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{texts.alohaVersionLabel}</div>
                                        <div style={{ margin: '0' }}>{illustrationDetailsObj.android?.aloha_version ?? texts.notAvailable}</div>
                                    </div>
                                    <div style={{ width: '45%' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{texts.alohaReleaseDateLabel}</div>
                                        <div style={{ margin: '0' }}>
                                            {illustrationDetailsObj.android?.date
                                                ? formatTime(illustrationDetailsObj.android.date)
                                                : texts.notAvailable}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <IllustrationViewer
                                combinedName={illustrationDetailsObj.combined_name}
                                imgUrl={illustrationDetailsObj.default.img_url}
                                imgUrlDark={illustrationDetailsObj.default.img_url_dark}
                                darkroomUrl={
                                    illustrationDetailsObj.default
                                        .darkroom_public_url
                                }
                                darkroomUrlDark={
                                    illustrationDetailsObj.default
                                        .darkroom_public_url_dark
                                }
                                variantSync={illustrationDetailsObj.variant_sync !== false}
                                illustrationName={
                                    texts.defaultIllustrationName
                                }
                                onGenerateDarkroomUrl={
                                    handleGenerateDarkroomUrl
                                }
                                isGeneratingDarkroomUrl={
                                    isGeneratingDarkroomUrl
                                }
                                illustrationType={IllustrationType.DEFAULT}
                                onEditButtonClick={() => {
                                    history.push(
                                        `${pathname}/${routes.UPDATE}`
                                    );
                                }}
                            />
                            <p className={styles.headingXL}>
                                {texts.regionalIllustrationsHeading}
                            </p>
                            <div className={styles.flagContainer}>
                                <Flag
                                    page
                                    info
                                    title={texts.vpnRequiredTitle}
                                    content={texts.vpnRequiredContent}
                                />
                            </div>
                            <div className={styles.gridContainer}>
                                {illustrationDetailsObj.regional_illustrations.filter((regionalIllustration) => {
                                    return !EXCLUDED_REGION_IDENTIFIERS.includes(regionalIllustration.region_identifier);
                                }).map(
                                    (regionalIllustration) =>
                                        getRegionalIllustrationListItem(
                                            regionalIllustration,
                                            illustrationDetailsObj.default
                                                .img_url
                                        )
                                )}
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            )}
        </React.Fragment>
    );
}

export default DefaultIllustrationDetailsPage;
